import { useState, type FormEvent } from 'react';

const FORM_ACTION = 'https://readdy.ai/api/form/db6dt5sbe24agvuu3iig';

const TOPICS = ['内容建议', '资源反馈', '合作洽谈', '问题反馈', '其他'];

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    // Anti-spam honeypot: silently accept when filled, but send nothing.
    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      formEl.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const str = String(value).trim();
      if (str) payload.append(key, str);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center rounded-lg border border-primary-200 bg-primary-50 px-6 py-12 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 text-background-50">
          <i className="ri-check-line text-3xl" />
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">反馈提交成功</h3>
        <p className="mt-2 text-sm text-foreground-600">感谢你的反馈，我们会认真阅读并尽快处理。</p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-5 whitespace-nowrap rounded-md border border-primary-300 px-5 py-2.5 text-sm font-medium text-primary-700 hover:bg-primary-100"
        >
          继续反馈
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="sigua-feedback-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-700">
            姓名 <span className="text-accent-600">*</span>
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-700">
            邮箱 <span className="text-accent-600">*</span>
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label htmlFor="fb-phone" className="mb-1.5 block text-sm font-medium text-foreground-700">
            电话（选填）
          </label>
          <input
            id="fb-phone"
            name="phone"
            type="tel"
            placeholder="方便时我们将与你联系"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label htmlFor="fb-topic" className="mb-1.5 block text-sm font-medium text-foreground-700">
            反馈类型 <span className="text-accent-600">*</span>
          </label>
          <select
            id="fb-topic"
            name="topic"
            required
            defaultValue=""
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="" disabled>请选择反馈类型</option>
            {TOPICS.map((topic) => (
              <option key={topic} value={topic}>{topic}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-700">
          反馈内容 <span className="text-accent-600">*</span>
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="写下你的建议或问题（最多 500 字）"
          className="w-full rounded-md border border-background-300 bg-background-50 p-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
        />
      </div>

      <input
        className="form-field-assist"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === 'error' && formError && (
        <p className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 px-3 py-2.5 text-sm text-accent-800">
          <i className="ri-error-warning-line mt-0.5" /> {formError}
        </p>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? (
            <>
              <i className="ri-loader-4-line animate-spin" /> 提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line" /> 提交反馈
            </>
          )}
        </button>
        <p className="text-xs text-foreground-500">我们仅将信息用于回复你的反馈，绝不对外泄露。</p>
      </div>
    </form>
  );
}